import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AlgoliaProvider from "@repo/shopify/algolia/AlgoliaProvider";
import { trackAlgoliaClickOn } from "@repo/shopify/algolia/insights";
import { useIsAlgoliaDown } from "@repo/shopify/algolia/status";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IProductDetailQuery } from "@repo/shopify/types/api/product";
import type { ISearchEdge, ISearchQuery } from "@repo/shopify/types/api/search";
import type { Product, ProductVariantConnection } from "@repo/shopify/types/generated/storefront.types";
import { getTotalProductCount } from "@repo/shopify/utils/collections";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Filter from "@molecules/Filter";
import ProductCard from "@molecules/ProductCard";

import { SEARCH_SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useAlgoliaSearchResult from "@hooks/algolia/useAlgoliaSearchResult";
import useHeaderScroll from "@hooks/useHeaderScroll";

import { ALGOLIA_ENABLED } from "@lib/algolia";

import useSearch from "@queries/useSearch";

import { useLayoutStore } from "@store/layout";

interface ISearchResultViewProps {
  queryParameter: string;
  searchData: ISearchQuery["search"] | undefined;
  products: ISearchEdge[];
  expandProducts: ISearchEdge[];
  hasNextPage: boolean;
  loading: boolean;
  onFetchMore: () => unknown;
}

function SearchResultView({
  queryParameter,
  searchData,
  products,
  expandProducts,
  hasNextPage,
  loading,
  onFetchMore,
}: ISearchResultViewProps) {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const { isPC } = useCheckDevice();

  useHeaderScroll();

  const { showFooter, setMeta, clearMeta, openSearch } = useLayoutStore();

  const [showFilter, setShowFilter] = useState<boolean>(false);

  useEffect(() => {
    if (!queryParameter) {
      navigate(URLS.HOME);
      setTimeout(() => {
        openSearch(true);
      }, 500);
    }

    showFooter(true);
  }, []);

  useEffect(() => {
    setMeta({ title: `Search Result for "${queryParameter}" - ${DEFAULT_TITLE}` });
    setShowFilter(false);
    window.scrollTo(0, 0);

    return () => {
      clearMeta();
    };
  }, [queryParameter]);

  const showProductList = useMemo(
    () => (searchParams.get("view") === "combined" ? products : expandProducts),
    [searchParams, expandProducts, products],
  );

  const totalProducts = useMemo(() => getTotalProductCount(searchData?.productFilters || []), [searchData]);

  return (
    <>
      {!searchData && loading ? (
        <Loading type="page" />
      ) : (
        <>
          {isPC ? (
            <>
              <section className="pc:col-span-2 pc:gap-5 pc:w-screen-col-span-2 sticky top-30 left-5 col-span-8 flex max-h-dvh gap-2 self-start overflow-y-auto pb-46">
                {showFilter ? (
                  <Filter
                    filters={searchData?.productFilters || []}
                    onClickClose={() => {
                      setShowFilter(false);
                    }}
                  />
                ) : (
                  <div className="flex flex-col gap-10">
                    <Button
                      as="text"
                      className="justify-end"
                      onClick={() => {
                        setShowFilter(!showFilter);
                      }}
                    >
                      {`Filter & Sort (${showProductList.length > 0 ? totalProducts : 0})`}
                    </Button>
                  </div>
                )}
              </section>
              <section className="pc:col-span-10 pc:grid-cols-5 pc:col-start-3 col-span-8 flex flex-col gap-2.5">
                {showProductList.length ? (
                  <>
                    {isPC ? <h1 className="leading-none">{`Search Result for "${queryParameter}"`}</h1> : null}
                    <section className="pc:tnt-gap-x pc:col-span-10 pc:grid-cols-5 pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                      {showProductList.map(({ node }, index) => (
                        <div
                          className="contents"
                          key={`${node.id}_${index}`}
                          onClickCapture={() => {
                            trackAlgoliaClickOn(node);
                          }}
                        >
                          <ProductCard
                            color={node.options.find((item) => item.name === "Color")?.values || []}
                            releaseDate={node?.release_date}
                            tags={node.tags}
                            title={node.title}
                            variants={node.variants}
                          />
                        </div>
                      ))}
                    </section>
                  </>
                ) : (
                  <>
                    {!loading ? (
                      <div className="pc:col-span-4 pc:gap-2.5 col-span-8 flex flex-col gap-2">
                        <div className="flex flex-col gap-1 leading-none">
                          {isPC ? <h1>{`Search Result for "${queryParameter}"`}</h1> : null}
                          <span>No results found for your search.</span>
                        </div>
                        <Link as="square" href={URLS.COLLECTIONS_ALL} variant="secondary">
                          View All Products
                        </Link>
                      </div>
                    ) : null}
                  </>
                )}

                {hasNextPage ? (
                  <Button
                    as="text"
                    className="pc:col-span-12 col-span-8 my-30 flex size-full justify-center"
                    loading={loading}
                    onClick={() => {
                      onFetchMore();
                    }}
                  >
                    {`Load More Products (${products.length} of ${totalProducts})`}
                  </Button>
                ) : (
                  <>
                    {showProductList.length > 20 ? (
                      <Button
                        as="text"
                        className="pc:col-span-12 col-span-8 my-30 flex place-self-center"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Back to Top
                      </Button>
                    ) : null}
                  </>
                )}
              </section>
            </>
          ) : (
            <>
              {showFilter ? (
                <Filter
                  filters={searchData?.productFilters || []}
                  onClickClose={() => {
                    setShowFilter(false);
                  }}
                />
              ) : (
                <>
                  {showProductList.length > 0 && (
                    <div className="sticky top-0 z-20 col-span-8 flex w-full justify-between gap-1 self-start bg-white py-2.5" id="filter">
                      <h1 className="text-base! leading-none">{`Search Result for "${queryParameter}"`}</h1>
                      <Button
                        as="wrapper"
                        onClick={() => {
                          setShowFilter(true);
                        }}
                      >
                        <Icon name="Filter" />
                      </Button>
                    </div>
                  )}
                </>
              )}

              <section className="col-span-8 flex flex-col gap-2.5">
                {showProductList.length ? (
                  <>
                    {isPC ? <h1 className="leading-none">{`Search Result for "${queryParameter}"`}</h1> : null}
                    <section className="pc:tnt-gap-x pc:gap-y-20 relative col-span-8 grid grid-cols-2 gap-x-4 gap-y-5">
                      {showProductList.map(({ node }, index) => (
                        <div
                          className="contents"
                          key={`${node.id}_${index}`}
                          onClickCapture={() => {
                            trackAlgoliaClickOn(node);
                          }}
                        >
                          <ProductCard
                            color={node.options.find((item) => item.name === "Color")?.values || []}
                            releaseDate={node?.release_date}
                            tags={node.tags}
                            title={node.title}
                            variants={node.variants}
                          />
                        </div>
                      ))}
                    </section>
                  </>
                ) : (
                  <>
                    {!loading ? (
                      <div className="pc:gap-2.5 col-span-8 flex flex-col gap-4">
                        <div className="flex flex-col gap-1 leading-none">
                          <h1>{`Search Result for "${queryParameter}"`}</h1>
                          <span>No results found for your search.</span>
                        </div>
                        <Link as="square" href={URLS.COLLECTIONS_ALL} variant="secondary">
                          View All Products
                        </Link>
                      </div>
                    ) : null}
                  </>
                )}

                {hasNextPage ? (
                  <Button
                    as="text"
                    className="col-span-8 my-30 flex size-full justify-center"
                    loading={loading}
                    onClick={() => {
                      onFetchMore();
                    }}
                  >
                    {`Load More Products (${products.length} of ${totalProducts})`}
                  </Button>
                ) : (
                  <>
                    {showProductList.length > 20 ? (
                      <Button
                        as="text"
                        className="col-span-8 my-30 flex place-self-center"
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Back to Top
                      </Button>
                    ) : null}
                  </>
                )}
              </section>
            </>
          )}
        </>
      )}
    </>
  );
}

function ShopifySearchResult() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const queryParameter = searchParams.get("q");

  const variables = useMemo(() => {
    const sortQueries = SEARCH_SORT.find((item) => item.name === searchParams.get("sort"));

    const filterQueries = [...searchParams]
      .filter(([key]) => key === "category" || key === "color" || key === "size")
      .map(([key, value]) => {
        const parseValue = JSON.parse(value || "[]") as string[];
        return parseValue.map((item) => (key === "category" ? { productType: item } : { variantOption: { name: key, value: item } }));
      })
      .flat();

    return {
      first: searchParams.get("view") === "seperate" ? 12 : 24, // 쿼리 1회에 조회하는 product 수
      types: "PRODUCT",
      query: queryParameter,
      sortKey: sortQueries?.value ?? "RELEVANCE",
      reverse: sortQueries?.reverse ?? true,
      prefix: "LAST",
      productFilters: filterQueries || [],
    };
  }, [queryParameter, searchParams]);

  const { fetchSearch, searchData: queryData, searchLoading: loading, fetchMoreSearch: fetchMore } = useSearch();

  // 최초 조회 + 검색어/필터 변경 시 재조회
  useEffect(() => {
    fetchSearch(variables);
  }, [variables]);

  const products = useMemo(() => {
    // 페이지 병합 과정에서 같은 상품이 중복될 수 있어 id 기준으로 한 번 걸러낸다
    const seen = new Set<string>();

    return (
      (queryData?.search?.edges as ISearchEdge[])
        ?.filter(({ node }) => Boolean(!(node as IProductDetailQuery["product"])?.vendor.includes("GWP")))
        ?.filter(({ node }) => (node as IProductDetailQuery["product"])?.hidden?.value !== "true")
        ?.filter(({ node }) => {
          if (!node?.id || seen.has(node?.id)) return false;
          seen.add(node?.id);
          return true;
        }) || []
    );
  }, [queryData?.search]);

  useEffect(() => {
    if (queryData && !queryData.search) navigate(URLS.NOT_FOUND);
  }, [queryData]);

  const getExpandList = (searchList: ISearchEdge[]) => {
    const expandList: ISearchEdge[] = [];

    searchList.forEach(({ node }) => {
      const colorOptions = (node as Product).options.find((item) => item.name === "Color")?.values;

      if (colorOptions) {
        colorOptions.forEach((color) => {
          const colorList = (node as Product).variants.edges.filter(({ node: variantNode }) =>
            variantNode.selectedOptions.find((option) => option.value === color),
          );

          const newList: ISearchEdge = {
            node: {
              ...node,
              options: (node as Product).options.map((option) => (option.name === "Color" ? { ...option, values: [color] } : option)),
              variants: { edges: colorList } as ProductVariantConnection,
            },
          } as ISearchEdge;

          expandList.push(newList);
        });
      }
    });

    return expandList;
  };

  /** 색상별로 쪼갠 목록 (view=combined 가 아닐 때 노출) */
  const expandProducts = useMemo(() => getExpandList(products), [products]);

  const hasNextPage = useMemo(() => queryData?.search?.pageInfo.hasNextPage || false, [queryData?.search?.pageInfo]);
  const handleFetchMore = async () => {
    await fetchMore();
  };

  return (
    <SearchResultView
      expandProducts={expandProducts}
      hasNextPage={hasNextPage}
      loading={loading}
      onFetchMore={handleFetchMore}
      products={products}
      queryParameter={queryParameter ?? ""}
      searchData={queryData?.search}
    />
  );
}

/** Algolia 검색. 순위 · 필터 · 카드 데이터 모두 Algolia 인덱스에서 온다. */
function AlgoliaSearchResult() {
  const [searchParams] = useSearchParams();

  const queryParameter = searchParams.get("q") ?? "";

  const { searchData, products, expandProducts, hasNextPage, loading, fetchMore } = useAlgoliaSearchResult(queryParameter, searchParams);

  return (
    <SearchResultView
      expandProducts={expandProducts}
      hasNextPage={hasNextPage}
      loading={loading}
      onFetchMore={fetchMore}
      products={products}
      queryParameter={queryParameter}
      searchData={searchData}
    />
  );
}

export default function SearchResult() {
  const [searchParams] = useSearchParams();
  const isAlgoliaDown = useIsAlgoliaDown();

  if (!ALGOLIA_ENABLED || isAlgoliaDown) return <ShopifySearchResult />;

  return (
    <AlgoliaProvider initialQuery={searchParams.get("q") ?? ""}>
      <AlgoliaSearchResult />
    </AlgoliaProvider>
  );
}
